<script setup>
import {
  reactive,
  ref,
  markRaw,
  watch,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import NavigationLink from '@/components/navigation/NavigationLink.vue';
import { agentVersion, staticUrl } from '@/config/index';
import AppHeader from '@/components/common/AppHeader.vue';
import PreviewSection from '@/components/preview/PreviewSection.vue';
import useReviewStore from '@/stores/review';
import useDataStore from '@/stores/data';
import {
  session,
  config as postmongerConfig,
  login,
} from '@/utils/postmonger';
import { apiClient } from '@/apis';
import { PERSONALIZATION_REGEX } from '@/utils/personalizationFields';
import { getImageInfo } from '@/utils/getImageInfo';
import messageTypeToSetData from '@/utils/messageTypeToSetData';
import toRawDeep from '@/utils/toRawDeep';
import { ROUTE_TO_ACCEPT_MESSAGE_TYPES } from '@/config/constant';
import { config as canvasConfig } from '@/utils/canvas';
import EmptySection from '@/components/common/EmptySection.vue';
import apiErrorMessage from '@/utils/apiErrorMessage';

const {
  t,
  locale,
} = useI18n();
const language = ref(locale.value);
const route = useRoute();
const {
  templateData,
  index,
} = storeToRefs(useReviewStore());
const { setTemplateData } = useReviewStore();
const { setMessageActivityName } = useDataStore();
const mode = ref('MC');

const getRunningData = async () => {
  const {
    runningData,
  } = postmongerConfig;

  runningData.templateId = runningData?.arguments?.execute?.inArguments?.[0]?.templateId || '';
  setMessageActivityName(runningData?.name || '');

  return runningData;
};

const data = reactive({
  messageType: '',
  kakaoProfile: '',
  fallback: {},
  alimtalkTemplateData: {},
  textmessage: {},
  friendtalkDefault: {},
  friendtalkWideItemList: {},
  friendtalkCommerce: {},
  friendtalkCarouselFeed: {},
  friendtalkCarouselCommerce: {},
  friendtalkPremiumVideo: {},
  brandDefault: {},
  brandWideItemList: {},
  brandCarouselFeed: {},
  brandCarouselCommerce: {},
  brandCommerce: {},
  brandPremiumVideo: {},
});

const setDisplay = async (templateId) => {
  if (templateData.value.templateId === templateId) {
    Object.entries(templateData.value).forEach(([key, value]) => {
      data[key] = value;
    });
    return;
  }

  let res = {};

  try {
    res = await apiClient.get(`/template/${templateId}`);
  } catch (error) {
    apiErrorMessage(error, '메시지 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    throw new Error('image not found');
  }

  const {
    template: {
      rejectPhone = '',
      outgoingPhone = '',
      adult,
      departmentId: department,
      advert = {},
      failback: fallback = {},
      kakaoProfile = '',
      messageType,
      recipientNumberField: recipientNumber,
      redelivery,
      additionalFields,
      // reserve,
      preTemplateId,
      pushNotification = {},
      target,
      personalizedField: personalizationFields,
    },
  } = res;

  data.templateId = templateId;
  data.kakaoProfile = kakaoProfile;
  data.outgoingPhone = outgoingPhone;
  data.rejectPhone = rejectPhone;
  data.recipientNumber = recipientNumber;
  data.department = department || '';
  data.messageType = messageType;
  data.redeliveryEnabled = redelivery?.enabled;
  data.isAdvert = advert?.enabled;
  data.targetingSettings = target;
  data.additionalFields = additionalFields;
  // data.reserve = reserve;
  data.isAdult = adult?.enabled;
  data.preTemplateId = preTemplateId;
  data.personalizationFields = [];
  data.isPushNotification = pushNotification?.enabled;

  if (ROUTE_TO_ACCEPT_MESSAGE_TYPES.kakao.includes(messageType)) {
    data.isAdvert = true;
  }

  if (ROUTE_TO_ACCEPT_MESSAGE_TYPES.alimtalk.includes(messageType)) {
    data.isAdvert = false;
  }

  if ([
    'ALIMTALK_BA',
    'ALIMTALK_EX',
    'ALIMTALK_AD',
    'ALIMTALK_MI',
  ].includes(messageType)) {
    let response = {};

    try {
      response = await apiClient.get(`/pre-template/${preTemplateId}`);
    } catch (error) {
      apiErrorMessage(error, '템플릿을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      throw new Error('pre template not found');
    }

    data.alimtalkTemplateData = {
      messageType: response.messageType || 'ALIMTALK_BA',
      emphasizeType: response.emphasizeType || 'NONE',
      content: response.content || '',
      security: response.security,
      buttons: response.buttons || [],
      itemList: {
        list: response?.itemList?.list || [],
        summary: {
          title: response?.itemList?.summary?.title || '',
          description: response?.itemList?.summary?.description || '',
        },
      },
      itemHighlight: {
        imageId: response?.itemHighlight?.imageId || '',
        title: response?.itemHighlight?.title || '',
        description: response?.itemHighlight?.description || '',
      },
      header: response?.header || '',
      imageId: response?.imageId || '',
      extra: response?.extra || '',
      title: response?.title || '',
      subTitle: response?.subTitle || '',
    };

    try {
      const dataString = JSON.stringify(response);
      const keys = [...new Set(dataString.match(PERSONALIZATION_REGEX))];

      keys.forEach((key, i) => {
        const stringKey = key.slice(2, key.length - 1);

        data.personalizationFields.push({
          index: i,
          label: key,
          value: personalizationFields[stringKey],
        });
      });
    } catch (error) {
      //
    }
  }

  if ([
    'BRANDTALK_TEXT',
    'BRANDTALK_IMAGE',
    'BRANDTALK_WIDE_IMAGE',
    'BRANDTALK_WIDE_ITEM_LIST',
    'BRANDTALK_COMMERCE',
    'BRANDTALK_CAROUSEL_FEED',
    'BRANDTALK_CAROUSEL_COMMERCE',
    'BRANDTALK_PREMIUM_VIDEO',
  ].includes(messageType)) {
    let response = {};

    try {
      response = await apiClient.get(`/pre-template/${preTemplateId}`);
    } catch (error) {
      apiErrorMessage(error, '템플릿을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      throw new Error('pre template not found');
    }

    Object.entries(response).forEach(([k, v]) => {
      res.template[k] = v;
    });

    data.templateName = response.name;
    data.adult = response.adult?.enabled;
    data.kakaoProfile = response.kakaoProfile;
    data.brandPreTemplateId = preTemplateId;

    try {
      const dataString = JSON.stringify(response);
      const keys = [...new Set(dataString.match(PERSONALIZATION_REGEX))];

      keys.forEach((key, i) => {
        const stringKey = key.slice(2, key.length - 1);

        data.personalizationFields.push({
          index: i,
          label: key,
          value: personalizationFields[stringKey],
        });
      });
    } catch (error) {
      //
    }
  }

  if (fallback && fallback?.id) {
    data.fallback = {
      enabled: true,
      department: fallback.departmentId,
      recipientNumber: fallback.recipientNumberField,
      messageType: fallback.messageType,
      outgoingPhone: fallback.outgoingPhone,
      rejectPhone: fallback.rejectPhone,
      content: fallback.content,
      subject: fallback.subject,
      image: {},
    };

    if (fallback?.imageId) {
      const image = await getImageInfo({
        id: fallback.imageId,
        imageType: 'textmessage',
      });

      data.fallback.image = {
        ...image,
        id: fallback.imageId,
        url: `${staticUrl}/textmessage/${fallback.imageId}`,
      };
    }
  }

  const result = await messageTypeToSetData(res.template);

  Object.entries(result).forEach(([key, value]) => {
    data[key] = value;
  });

  setTemplateData(toRawDeep(data));
};

const postmongerInit = async () => {
  await login();

  const { templateId } = await getRunningData();

  setDisplay(templateId);
};

const addEvent = () => {
  const initActivityRunningModal = (item) => {
    const temp = postmongerConfig;
    temp.type = 'running';
    temp.runningData = item;

    setDisplay();
  };

  session.on('initActivityRunningModal', initActivityRunningModal);
};

const initPage = () => {
  if (postmongerConfig.type) {
    postmongerInit();
    return;
  }

  if (canvasConfig.templateId) {
    setDisplay(canvasConfig.templateId);
    return;
  }

  addEvent();
};

initPage();

watch(index, (i) => {
  switch (data.messageType) {
    case 'FRIENDTALK_WIDE_ITEM_LIST':
      data.friendtalkWideItemList.index = i;
      break;
    case 'FRIENDTALK_CAROUSEL_FEED':
      data.friendtalkCarouselFeed.index = i;
      break;
    case 'FRIENDTALK_CAROUSEL_COMMERCE':
      data.friendtalkCarouselCommerce.index = i;
      break;
    case 'BRANDTALK_WIDE_ITEM_LIST':
      data.brandWideItemList.index = i;
      break;
    case 'BRANDTALK_CAROUSEL_FEED':
      data.brandCarouselFeed.index = i;
      break;
    case 'BRANDTALK_CAROUSEL_COMMERCE':
      data.brandCarouselCommerce.index = i;
      break;
    default:
      break;
  }
}, { immediate: true });

watch(templateData, (item) => {
  data.messageType = item.messageType;
  data.kakaoProfile = item.kakaoProfile;
  data.fallback = item.fallback;
  data.alimtalkTemplateData = item.alimtalkTemplateData;
  data.textmessage = item.textmessage;
  data.friendtalkDefault = item.friendtalkDefault;
  data.friendtalkWideItemList = item.friendtalkWideItemList;
  data.friendtalkCommerce = item.friendtalkCommerce;
  data.friendtalkCarouselFeed = item.friendtalkCarouselFeed;
  data.friendtalkCarouselCommerce = item.friendtalkCarouselCommerce;
  data.friendtalkPremiumVideo = item.friendtalkPremiumVideo;
  data.brandDefault = item.brandDefault;
  data.brandWideItemList = item.brandWideItemList;
  data.brandCarouselFeed = item.brandCarouselFeed;
  data.brandCarouselCommerce = item.brandCarouselCommerce;
  data.brandCommerce = item.brandCommerce;
  data.brandPremiumVideo = item.brandPremiumVideo;
}, { deep: true, immediate: true });

watch(language, (value) => {
  locale.value = value;
  localStorage.setItem('locale', value);
  document.querySelector('html').setAttribute('lang', value);
}, { immediate: true });

const navigationArray = reactive([
  {
    key: 'details',
    className: 'nav__text--setting',
    title: 'navigation.details',
    to: {
      path: '/details',
      query: route.query,
    },
    clickEvent: () => {
      document.body.dispatchEvent(new Event('close-mentionList'));
    },
    components: markRaw(NavigationLink),
    // scope: ['2100'],
  },
  {
    key: 'result',
    className: 'nav__text--result',
    title: 'navigation.result',
    to: {
      path: '/result',
      query: route.query,
    },
    clickEvent: () => {
      document.body.dispatchEvent(new Event('close-mentionList'));
    },
    components: markRaw(NavigationLink),
    // scope: ['2200', '2210'],
  },
]);

const displayList = computed(() => navigationArray);

const languageList = reactive([
  {
    label: computed(() => t('navigation.language.korean')),
    value: 'ko',
  },
  {
    label: computed(() => t('navigation.language.english')),
    value: 'en',
  },
]);

const query = {};
const search = (window.location.search || '').slice(1);

search.split('&').forEach((str) => {
  const [key, value] = str.split('=');

  query[key] = value;
});

if (query?.nonce && query?.organizationId) {
  mode.value = 'CORE';
}

const header = computed(() => {
  const map = {
    MC: AppHeader,
    CORE: EmptySection,
  };

  return map[mode.value] || EmptySection;
});

const noHeaderClass = computed(() => mode.value === 'CORE');

</script>
<template>
  <component :is="header" />
  <div
    class="wrap"
    :class="{ 'no-header': noHeaderClass }"
  >
    <div class="navigation-wrap">
      <div class="navigation">
        <strong>{{ t('navigation.mainTitle') }}<br />{{ t('navigation.subTitle') }}</strong>
        <p>Version {{ agentVersion }}</p>
        <nav>
          <component
            v-for="item in displayList"
            :key="`navigation${item.key}`"
            :is="item.components"
            :to="item.to"
            :class-name="item.className"
            :title="item.title"
            :active="true"
            @click-event="item.clickEvent"
          />
          <div class="language">
            <p>{{ t('navigation.language.select') }}</p>
            <v-select
              v-model="language"
              :items="languageList"
              class="font-size-11"
              item-title="label"
              item-value="value"
              :menu-props="{
                contentClass: ['font-size-11'],
                scrim: true,
                scrollStrategy: 'close',
              }"
            ></v-select>
          </div>
        </nav>
      </div>
    </div>
    <div class="container">
      <slot />
      <PreviewSection
        :resizable="true"
        :message-type="data.messageType"
        :textmessage="data.textmessage"
        :fallback="data.fallback"
        :kakao-profile="data.kakaoProfile"
        :alimtalk-template-data="data.alimtalkTemplateData"
        :friendtalk-default="data.friendtalkDefault"
        :friendtalk-wide-item-list="data.friendtalkWideItemList"
        :friendtalk-carousel-feed="data.friendtalkCarouselFeed"
        :friendtalk-carousel-commerce="data.friendtalkCarouselCommerce"
        :friendtalk-commerce="data.friendtalkCommerce"
        :friendtalk-premium-video="data.friendtalkPremiumVideo"
        :brand-default="data.brandDefault"
        :brand-wide-item-list="data.brandWideItemList"
        :brand-carousel-feed="data.brandCarouselFeed"
        :brand-carousel-commerce="data.brandCarouselCommerce"
        :brand-commerce="data.brandCommerce"
        :brand-premium-video="data.brandPremiumVideo"
        :swipe-enabled="true"
      />
    </div>
  </div>
</template>
